import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, catchError, filter, of, skip, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { TeamEventMeetingType } from '@vendasta/meetings';
import { MeetingType } from '../interface';
import { BookingStep, BookMeNowStoreService, BusinessDetails, Errors } from '../book-me-now-store.service';
import {
  getTimezone,
  getZoneFromTimezone,
  hasContactPrefill,
  meetingTypeToServiceEventType,
  parseContactPrefillFromParams,
  parseQueryString,
} from '../util';
import { consumeInitialQueryString } from '../initial-query';

export interface BreadcrumbStep {
  step: BookingStep;
  label: string;
  active: boolean;
  completed?: boolean;
  skipped?: boolean;
}

const SERVICE_STEP_LABELS: BookingStep[] = ['scheduling', 'provider', 'datetime', 'confirm'];
const SERVICE_STEP_DISPLAY = [
  'BOOKING_STEPS.SERVICES',
  'BOOKING_STEPS.PROVIDER',
  'BOOKING_STEPS.DATE_TIME',
  'BOOKING_STEPS.CONFIRM',
];

const SERVICE_STEP_LABELS_NO_PROVIDER: BookingStep[] = ['scheduling', 'datetime', 'confirm'];
const SERVICE_STEP_DISPLAY_NO_PROVIDER = ['BOOKING_STEPS.SERVICES', 'BOOKING_STEPS.DATE_TIME', 'BOOKING_STEPS.CONFIRM'];

const BOOK_STEP_LABELS: BookingStep[] = ['provider', 'datetime', 'confirm'];
const BOOK_STEP_DISPLAY = ['BOOKING_STEPS.PROVIDER', 'BOOKING_STEPS.DATE_TIME', 'BOOKING_STEPS.CONFIRM'];

const BOOK_STEP_LABELS_NO_PROVIDER: BookingStep[] = ['datetime', 'confirm'];
const BOOK_STEP_DISPLAY_NO_PROVIDER = ['BOOKING_STEPS.DATE_TIME', 'BOOKING_STEPS.CONFIRM'];

@Component({
  selector: 'app-booking-layout',
  standalone: false,
  templateUrl: './booking-layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-layout.component.scss'],
  host: { '(document:keydown.escape)': 'closeBusinessInfo()' },
})
export class BookingLayoutComponent implements OnInit, OnDestroy {
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  showProviderStep = signal(false);
  providerStepSkipped = signal(false);
  providerStepDetermined = signal(false);
  logoUrl = signal<string | null>(null);
  businessDetails = signal<BusinessDetails | null>(null);
  businessInfoOpen = signal(false);

  readonly businessName = computed(() => this.businessDetails()?.name?.trim() || '');
  readonly businessAddress = computed(() => this.businessDetails()?.address?.trim() || '');
  readonly businessPhone = computed(() => this.businessDetails()?.phoneNumber?.trim() || '');
  readonly businessWebsite = computed(() => this.businessDetails()?.website?.trim() || '');

  readonly businessAddressShort = computed(() => this.businessAddress().split(',')[0]?.trim() || '');

  readonly businessAddressRest = computed(() => {
    const address = this.businessAddress();
    const separatorIndex = address.indexOf(',');
    return separatorIndex >= 0 ? address.slice(separatorIndex + 1).trim() : '';
  });

  readonly businessWebsiteUrl = computed(() => {
    const website = this.businessWebsite();
    if (!website) return '';
    return /^https?:\/\//i.test(website) ? website : `https://${website}`;
  });

  readonly businessMapsUrl = computed(() => {
    const address = this.businessAddress();
    if (!address) return '';
    const name = this.businessName();
    const query = name ? `${name}, ${address}` : address;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  });
  pageTitle = 'BOOK.PAGE_TITLE';
  hideHeader = signal(false);
  isRescheduleMode = false;

  /** Error display from store */
  errorDisplay$: Observable<Errors>;
  shouldDisplayError$: Observable<boolean>;

  readonly defaultLogoUrl = '/assets/images/meetingscheduler-logo.png';

  displayLogoUrl = computed(() => {
    const url = this.logoUrl();
    if (url === null) return '';
    if (url) return url;
    // Confirmed no logo: a real business profile shows nothing, not Vendasta's own branding.
    return this.businessName() ? '' : this.defaultLogoUrl;
  });

  isSingleEventRoute = signal(false);

  /** Current booking step — null until step is determined, prevents rendering wrong component on init */
  currentStep = signal<BookingStep | null>(null);
  activeStepIndex = signal(0);

  steps = computed<BreadcrumbStep[]>(() => {
    const active = this.activeStepIndex();
    const show = this.showProviderStep();
    const skipped = this.providerStepSkipped();
    let labels: string[];
    let stepIds: BookingStep[];
    if (this.isSingleEventRoute()) {
      labels = show ? BOOK_STEP_DISPLAY : BOOK_STEP_DISPLAY_NO_PROVIDER;
      stepIds = show ? BOOK_STEP_LABELS : BOOK_STEP_LABELS_NO_PROVIDER;
    } else {
      labels = show ? SERVICE_STEP_DISPLAY : SERVICE_STEP_DISPLAY_NO_PROVIDER;
      stepIds = show ? SERVICE_STEP_LABELS : SERVICE_STEP_LABELS_NO_PROVIDER;
    }
    return labels.map((label, i) => {
      const step = stepIds[i];
      const isSkippedProvider = skipped && step === 'provider';
      return {
        step,
        label,
        active: i === active,
        completed: i < active && !isSkippedProvider,
        skipped: isSkippedProvider,
      };
    });
  });

  /** Breadcrumbs only navigate backwards: a completed step can be reopened, nothing ahead can be skipped to. */
  goToStep(step: BreadcrumbStep): void {
    if (!step.completed) return;
    this.storeService.setBookingStep(step.step);
  }

  ngOnInit(): void {
    this.captureContactPrefill();

    // Error display from store
    this.errorDisplay$ = this.storeService.errorDisplay$;
    this.shouldDisplayError$ = this.errorDisplay$.pipe(map((err) => Boolean(err)));

    // Check if embedded in widget (hide_header=true query param)
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.hideHeader.set(params['hide_header'] === 'true');
    });

    this.isRescheduleMode = this.route.snapshot.data['mode'] === 'reschedule';
    const isSingleEvent = this.router.url.startsWith('/book/');
    this.isSingleEventRoute.set(isSingleEvent);
    if (this.isRescheduleMode) {
      this.initRescheduleMode();
    } else {
      this.initBookingMode();
    }

    this.storeService.shouldShowProviderStep$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((show) => {
      this.showProviderStep.set(show);
    });

    this.storeService.providerStepDetermined$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((determined) => {
      this.providerStepDetermined.set(determined);
    });

    this.storeService.providerStepSkipped$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((skipped) => {
      this.providerStepSkipped.set(skipped);
    });

    this.storeService.bookingStep$
      .pipe(skip(isSingleEvent ? 1 : 0), takeUntilDestroyed(this.destroyRef))
      .subscribe((step) => {
        this.currentStep.set(step);
        const show = this.showProviderStep();
        let stepLabels: BookingStep[];
        if (isSingleEvent) {
          stepLabels = show ? BOOK_STEP_LABELS : BOOK_STEP_LABELS_NO_PROVIDER;
        } else {
          stepLabels = show ? SERVICE_STEP_LABELS : SERVICE_STEP_LABELS_NO_PROVIDER;
        }
        this.activeStepIndex.set(stepLabels.indexOf(step));
      });

    this.storeService.businessDisplayLogoUrl$
      .pipe(
        filter((url): url is string => url !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((url) => {
        this.logoUrl.set(url || '');
      });

    this.storeService.businessDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((details) => {
      this.businessDetails.set(details);
    });

    this.storeService.calendar$.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe((calendar) => {
      if (calendar?.displayLogoUrl) {
        this.logoUrl.set(calendar.displayLogoUrl);
      } else {
        this.logoUrl.set('');
      }
    });

    if (isSingleEvent) {
      const calendarId = this.route.snapshot.params['calendarId'];
      if (calendarId) {
        this.storeService
          .loadCalendar(calendarId)
          .pipe(
            catchError(() => EMPTY),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    }
  }

  toggleBusinessInfo(): void {
    this.businessInfoOpen.update((open) => !open);
  }

  closeBusinessInfo(): void {
    this.businessInfoOpen.set(false);
  }

  selectNewTime(): void {
    this.storeService.setErrorDisplay(undefined);
    const hasEvents = this.storeService.getSelectedBookingEvents().length > 0;
    this.storeService.setBookingStep(hasEvents ? 'datetime' : 'scheduling');
  }

  /**
   * Captures the entry URL's contact params into the store so the confirm step
   * can prefill them, and stashes the raw entry query string so the custom-field
   * prefill reads the same source. Params are intentionally left in the URL;
   * contact PII is kept out of lead-routing metadata by collectTrackingMetadata's
   * exclusion (not by stripping the address bar).
   */
  private captureContactPrefill(): void {
    const search = this.readEntryQueryString();
    if (!search) {
      // A plain (non-personalized) link must not inherit a previous contact's
      // details left in the root singleton store from an earlier booking.
      this.storeService.clearContactPrefill();
      this.storeService.clearPrefillSearch();
      return;
    }
    this.storeService.setPrefillSearch(search);
    const prefill = parseContactPrefillFromParams(parseQueryString(search));
    if (hasContactPrefill(prefill)) {
      this.storeService.setContactPrefill(prefill);
    } else {
      this.storeService.clearContactPrefill();
    }
  }

  /**
   * The entry URL's query string. Reads window.location (the router snapshot is
   * unreliable across this flow, mirroring resolveBookingSource) and falls back
   * to the load-time snapshot when a redirect has already stripped the address
   * bar. The snapshot is consumed once, so an in-session remount at a plain URL
   * cannot resurrect a prior link's params.
   */
  private readEntryQueryString(): string {
    const live = typeof window !== 'undefined' && window.location ? window.location.search : '';
    const snapshot = consumeInitialQueryString();
    return live || snapshot;
  }

  ngOnDestroy(): void {
    this.storeService.setErrorDisplay(undefined);
    this.storeService.resetBookingStep();
    if (this.isRescheduleMode) {
      this.storeService.setRescheduleMode(false);
    }
  }

  private initBookingMode(): void {
    if (this.isSingleEventRoute()) {
      const calendarId = this.route.snapshot.params['calendarId'];
      const meetingTypeId = this.route.snapshot.params['meetingTypeId'];
      if (calendarId && meetingTypeId) {
        this.storeService
          .loadMeetingTypeForGivenCalendarIdAndSlug(calendarId, meetingTypeId)
          .pipe(
            catchError(() => of(null)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((mt) => {
            if (mt) {
              const type = mt.meetingType ?? TeamEventMeetingType.ROUND_ROBIN;
              const allHosts = mt.hostUsers || [];
              const hasConnected = allHosts.some((h: { isConnected?: boolean }) => h.isConnected);
              const connectedHosts = hasConnected
                ? allHosts.filter((h: { isConnected?: boolean }) => h.isConnected)
                : allHosts;
              const needsProvider = type === TeamEventMeetingType.CLIENT_CHOICE && connectedHosts.length > 1;
              this.storeService.setShouldShowProviderStep(needsProvider);
              this.populateSingleEventData(mt, connectedHosts);
              this.storeService.setBookingStep(needsProvider ? 'provider' : 'datetime');
            } else {
              this.storeService.setShouldShowProviderStep(true);
              this.storeService.setBookingStep('provider');
            }
          });
      } else {
        this.storeService.setShouldShowProviderStep(true);
        this.storeService.setBookingStep('provider');
      }
    } else {
      const currentStep = this.storeService.getBookingStep();
      if (!currentStep || currentStep === 'scheduling') {
        this.storeService.setBookingStep('scheduling');
      }
    }
  }

  private populateSingleEventData(
    mt: MeetingType,
    connectedHosts: { id?: string; name?: string; isConnected?: boolean }[],
  ): void {
    const eventData = meetingTypeToServiceEventType(mt);
    this.storeService.setSelectedBookingEvents([eventData]);
    this.storeService.setBookingOriginId(mt.id || '');

    if (connectedHosts.length === 1) {
      const host = connectedHosts[0];
      const providerMap = new Map<string, string>();
      const hostMap = new Map<string, string>();
      providerMap.set(mt.id || '', host.name || '');
      hostMap.set(mt.id || '', host.id || '');
      this.storeService.setEventProviderMap(providerMap);
      this.storeService.setEventHostMap(hostMap);
      this.storeService.setSelectedHost(host.id || '');
    }
  }

  private initRescheduleMode(): void {
    this.storeService.setRescheduleMode(true);
    this.pageTitle = 'RESCHEDULE.PAGE_TITLE';
    this.isSingleEventRoute.set(true);
    this.storeService.setBookingStep('datetime');

    const meetingId = this.route.snapshot.params['meetingId'];
    this.storeService.setMeetingToRescheduleOrCancel(meetingId);
    this.loadRescheduleMeetingData(meetingId);
  }

  /**
   * Single V2 API call provides all meeting data.
   * meetings[] is always populated (1 entry for single, N for batch),
   * each with eventTypeId and userId for target reconstruction.
   */
  private loadRescheduleMeetingData(meetingId: string): void {
    this.storeService
      .getGuestBookedMeetingV2({ id: meetingId })
      .pipe(
        tap((v2) => {
          this.storeService.setGuestBookedMeetingV2(v2);
          this.initStoreFromV2(v2);
          if (!v2.businessDetails?.businessLogoUrl) {
            this.logoUrl.set(v2.businessLogo || '');
          }

          const targets = (v2.meetings || []).map((m) => ({
            eventTypeId: m.eventTypeId,
            // Multi-host meetings (>1 host) must omit userId so the server can select an available host
            userId: (m.hostUserNames?.length ?? 0) > 1 ? undefined : m.userId,
            order: m.order,
          }));
          this.storeService.setRescheduleTargets(targets);
        }),
        catchError((err) => {
          this.storeService.handleMeetingError(err, 'reschedule');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private initStoreFromV2(v2: {
    meetings?: {
      startTime?: Date;
      endTime?: Date;
      timeZone?: { id?: string };
    }[];
    timeZone?: { id?: string };
  }): void {
    const meetings = v2.meetings || [];
    if (meetings.length === 0) return;

    const firstMeeting = meetings[0];
    const lastMeeting = meetings[meetings.length - 1];
    const tz = firstMeeting?.timeZone?.id || v2.timeZone?.id || getTimezone();

    // For single: start/end from the only meeting.
    // For batch: first start → last end (full chain duration for reschedule correction).
    this.storeService.setOriginalMeetingTimeSlot({
      start: firstMeeting.startTime,
      end: lastMeeting.endTime,
      timezone: {
        region: tz,
        zone: getZoneFromTimezone(tz, firstMeeting.startTime?.getTimezoneOffset?.() ?? 0),
      },
    });

    if (!this.storeService.getTimezoneChanged()) {
      this.storeService.setTimezone(tz, true);
    }
  }
}
